import { useEffect, useMemo, useState } from 'react'

import { useI18n } from '@/lib/i18n'
import type { WaitingInput } from '@/lib/waiting-state'
import { resolveWaitingPresentation } from '@/lib/waiting-state'
import { WaitingTipsCarousel } from './WaitingTipsCarousel'

export interface WaitingIndicatorProps
  extends Omit<WaitingInput, 'lastSuccessAgeMs'> {
  lastSuccessAt?: number | null
  testId?: string
}

const TICK_MS = 1000

export function WaitingIndicator({
  lastSuccessAt,
  testId = 'waiting-indicator',
  ...waiting
}: WaitingIndicatorProps) {
  const { t } = useI18n()
  const [nowMs, setNowMs] = useState(0)

  useEffect(() => {
    if (typeof lastSuccessAt !== 'number') {
      setNowMs(0)
      return
    }

    setNowMs(Date.now())
    const timer = setInterval(() => setNowMs(Date.now()), TICK_MS)
    return () => clearInterval(timer)
  }, [lastSuccessAt])

  const lastSuccessAgeMs
    = typeof lastSuccessAt === 'number' && nowMs > 0
      ? Math.max(0, nowMs - lastSuccessAt)
      : null

  const thinkingKey = waiting.thinkingSeatNames?.join(' ')
  const waitingOnKey = waiting.waitingOnNames?.join(' ')
  const presentation = useMemo(
    () =>
      resolveWaitingPresentation({ ...waiting, lastSuccessAgeMs }),
    [
      lastSuccessAgeMs,
      thinkingKey,
      waiting.delivery,
      waiting.gameStatus,
      waiting.hasNextInput,
      waiting.isFetchFailing,
      waiting.shouldHideSubphaseDetail,
      waitingOnKey,
    ],
  )

  const message = t(presentation.i18nKey, presentation.i18nVars)
  const showTips = presentation.kind === 'aiActing'
    || presentation.kind === 'fallback'
    || presentation.kind === 'generic'
    || presentation.kind === 'hiddenNight'
    || presentation.kind === 'otherHuman'
    || presentation.kind === 'reconnecting'
    || presentation.kind === 'stale'

  if (presentation.kind === 'hiddenNight') {
    return (
      <div
        className="space-y-1"
        data-testid={testId}
        data-waiting-kind={presentation.kind}
      >
        <span aria-live="polite" className="sr-only" role="status">
          {t('play.waiting.default')}
        </span>
        <WaitingTipsCarousel />
      </div>
    )
  }

  if (presentation.kind === 'aiActing') {
    return (
      <div
        className="space-y-1"
        data-testid={testId}
        data-waiting-kind={presentation.kind}
      >
        <span aria-live="polite" className="sr-only" role="status">
          {message}
        </span>
        <WaitingTipsCarousel />
      </div>
    )
  }

  return (
    <div
      className="space-y-1"
      data-testid={testId}
      data-waiting-kind={presentation.kind}
    >
      <p
        aria-live="polite"
        className="flex items-center gap-2 text-sm text-muted-foreground"
        role="status"
      >
        <span aria-hidden="true" className="animate-spin">
          ⏳
        </span>
        <span>{message}</span>
      </p>
      {showTips && <WaitingTipsCarousel />}
    </div>
  )
}
